import createClient, { type Middleware } from 'openapi-fetch';

// The typed clients. Each service and version has one, made from the types generated from its OpenAPI document
// (see scripts/openapi.mjs); the paths of the call, its body, its query and its answer are all checked against that document.
// What every client does the same way lives here: it sends the session cookie, gives every POST an idempotency key, and
// turns a refusal into the standard error shape.

// What a refusal carries: the standard list of errors, as the service sends them.
export type ApiErrorItem = { error_key?: string; error_message?: string; form_uuid?: string; field_name?: string; object_uuid?: string };
export type ApiFailure = { status?: number; data: ApiErrorItem[] | Record<string, never> };

// No real response body to read (a network failure, or an answer that is not JSON). Deliberately has no `message`:
// callers supply their own fallback text instead of treating this as a message from the server.
const NO_RESPONSE_DATA = {};

export const idempotencyKeys: Middleware = {
  onRequest({ request }) {
    if (request.method === 'POST' && !request.headers.has('Idempotency-Key')) {
      const key = globalThis.crypto?.randomUUID?.();
      if (key) request.headers.set('Idempotency-Key', key);
    }
    return request;
  },
};

// Makes the client for one service and version. `base` is where the service is reached; a function is read at the first
// call, for the apps whose own service is only known once the app has started.
export const serviceClient = <Paths extends {}>(base: string | (() => string)) => {
  let client: ReturnType<typeof createClient<Paths>> | undefined;
  return () => {
    if (!client) {
      client = createClient<Paths>({ baseUrl: typeof base === 'function' ? base() : base, credentials: 'include', mode: 'cors' });
      client.use(idempotencyKeys);
    }
    return client;
  };
};

type Settled<D, E> = Promise<{ data?: D; error?: E; response: Response }>;

// Waits for a call and answers with its data (null when the answer has none), or rejects with {status, data}: the
// standard errors for a refusal, an empty object for a failure that has no answer to read.
export const unwrap = async <D, E>(call: Settled<D, E>) => {
  let settled;
  try {
    settled = await call;
  } catch {
    return Promise.reject<{ data: D; status: number }>({ data: NO_RESPONSE_DATA });
  }
  const { data, error, response } = settled;
  if (response.ok) {
    return { data: (response.status === 204 || data === undefined ? null : data) as D, status: response.status };
  }
  return Promise.reject<{ data: D; status: number }>({ status: response.status, data: error === undefined ? NO_RESPONSE_DATA : error });
};
